import { useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Layout from '../componentes/Layout';
import Aviso from '../componentes/Aviso';
import FormularioDoBalcao from '../componentes/FormularioDoBalcao';
import { cadastrarPecaPeloBalcao, tiposDePeca } from '../lib/consultas';
import {
  PRECO_PARA_CONFIRMAR, RASCUNHO_VAZIO, dadosDoRascunho, problemaDoRascunho,
  type RascunhoDoBalcao,
} from '../lib/cadastroDoBalcao';
import { brl } from '../lib/formato';
import { podeConferirCadastro, useSessao } from '../lib/sessao';
import { mensagemDeErro } from '../lib/supabase';
import { sugerirTipo } from '../lib/tipoSugerido';

// =============================================================================
// CADASTRAR PEÇA PELO BALCÃO (fila de conferência, 093 — PR 2, 08/10/2026)
//
// O pedido do dono: um cadastro "extremamente fácil" para os colaboradores,
// sem que eles possam "fazer nenhum tipo de merda". A resposta tem duas
// metades: a TELA é curta (sete campos, em coluna, sem custo nem margem), e o
// BANCO trava — a peça do vendedor nasce aguardando liberação e não vende até
// o gerente ou o dono liberar (cadastrar_peca_pelo_balcao, conferencia.sql).
// A do gerente e a do dono nascem liberadas (decisão c).
//
// A porta é o vazio da busca ("Cadastrar peça agora"), que traz o código que
// se buscou: obriga a procurar antes, e é o que evita a peça em dobro. O banco
// ainda recusa código repetido, e diz de qual peça ele é.
//
// Não é ramo do Cadastro.tsx de propósito: aquele tem 1.700 linhas de cadastro
// completo, e cada `if (vendedor)` lá dentro era um lugar a mais para vazar
// custo.
// =============================================================================

export default function CadastroDoBalcao() {
  const { papel } = useSessao();
  const navigate = useNavigate();
  const cliente = useQueryClient();
  const local = useLocation();
  // O código bipado na busca segue junto, pronto para o próximo bip.
  const codigoBipado = (local.state as { codigo?: string } | null)?.codigo ?? '';
  const [r, setR] = useState<RascunhoDoBalcao>(() => ({ ...RASCUNHO_VAZIO, codigo: codigoBipado }));
  // O "confere?" do preço alto: perguntando agora, e já respondido para ESTE preço.
  const [perguntandoPreco, setPerguntandoPreco] = useState(false);
  const [precoConferido, setPrecoConferido] = useState(false);
  const [aviso, setAviso] = useState<string | null>(null);
  const [cadastrada, setCadastrada] = useState(false);

  const tipos = useQuery({ queryKey: ['tipos-de-peca'], queryFn: tiposDePeca, staleTime: 10 * 60_000 });
  const sugerido = useMemo(() => {
    const slug = sugerirTipo(r.descricao, tipos.data ?? []);
    return (tipos.data ?? []).find((t) => t.slug === slug) ?? null;
  }, [r.descricao, tipos.data]);

  const libera = podeConferirCadastro(papel);

  const cadastrar = useMutation({
    mutationFn: () => cadastrarPecaPeloBalcao(dadosDoRascunho(r, sugerido?.slug ?? null)),
    onSuccess: (id) => {
      cliente.invalidateQueries({ queryKey: ['buscar'] });
      cliente.invalidateQueries({ queryKey: ['fila-conferencia'] });
      cliente.invalidateQueries({ queryKey: ['sino', 'para-liberar'] });
      // Gerente e dono: a peça nasceu liberada — a ficha é o próximo passo
      // (foto, aplicação, custo, pelo cadastro completo).
      if (libera) {
        navigate(`/peca/${id}`);
        return;
      }
      setCadastrada(true);
    },
  });

  const mudar = (novo: RascunhoDoBalcao) => {
    setR(novo);
    setAviso(null);
    // Mexeu no preço depois do "confere?": pergunta de novo.
    if (novo.preco !== r.preco) {
      setPrecoConferido(false);
      setPerguntandoPreco(false);
    }
  };

  const tentar = () => {
    const problema = problemaDoRascunho(r);
    if (problema) {
      setAviso(problema);
      return;
    }
    if ((r.preco ?? 0) > PRECO_PARA_CONFIRMAR && !precoConferido) {
      setPerguntandoPreco(true);
      return;
    }
    setAviso(null);
    cadastrar.mutate();
  };

  const recomecar = () => {
    setR(RASCUNHO_VAZIO);
    setPrecoConferido(false);
    setPerguntandoPreco(false);
    setAviso(null);
    setCadastrada(false);
    cadastrar.reset();
  };

  if (cadastrada) {
    return (
      <Layout titulo="Cadastrar peça (balcão)">
        <div className="space-y-4">
          <Aviso tipo="ok">
            Cadastrada. Ela já aparece na busca, marcada, e vende depois que o gerente ou o dono
            liberar. Para vender agora, use a PEÇA DIVERSA.
          </Aviso>
          <div className="flex flex-col gap-2 sm:flex-row">
            <Link to="/venda" className="botao-primario">Voltar à venda</Link>
            <Link to="/conferencia" className="botao-secundario">Minhas peças</Link>
            <button type="button" className="botao-secundario" onClick={recomecar}>
              Cadastrar outra
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout titulo="Cadastrar peça (balcão)">
      <form
        className="space-y-4"
        onSubmit={(e) => e.preventDefault()}
        onKeyDown={(e) => {
          // O leitor USB digita o código e termina com Enter — e Enter em
          // <input> submete o formulário: era o bipe cadastrando a peça pela
          // metade (visto no balcão em 07/08/2026, Cadastro.tsx). Cadastrar é
          // SÓ no botão.
          if (e.key === 'Enter' && (e.target as HTMLElement).tagName === 'INPUT') e.preventDefault();
        }}
      >
        <p className="text-sm text-mute">
          {libera
            ? 'A peça que você cadastra aqui já nasce liberada para vender. Foto, aplicação e o resto ficam no cadastro completo, na ficha.'
            : 'A peça entra na busca na hora, marcada, e vende depois que o gerente ou o dono liberar.'}
        </p>

        <section className="cartao p-4">
          <FormularioDoBalcao valores={r} aoMudar={mudar} tipos={tipos.data ?? []}
                              tipoSugerido={sugerido} focarNome />
        </section>

        {aviso ? <Aviso tipo="erro">{aviso}</Aviso> : null}

        {perguntandoPreco && (
          <div className="space-y-2">
            <Aviso tipo="alerta">
              Preço de balcão {brl(r.preco)} — confere? Acima de {brl(PRECO_PARA_CONFIRMAR)} a gente
              pergunta, porque vírgula trocada vira preço errado no balcão.
            </Aviso>
            <div className="flex flex-col gap-2 sm:flex-row">
              <button type="button" className="botao-primario" disabled={cadastrar.isPending}
                      onClick={() => { setPrecoConferido(true); setPerguntandoPreco(false); cadastrar.mutate(); }}>
                Confere, cadastrar
              </button>
              <button type="button" className="botao-secundario" onClick={() => setPerguntandoPreco(false)}>
                Corrigir o preço
              </button>
            </div>
          </div>
        )}

        {/* O erro do banco INTEIRO: o código repetido diz de qual peça ele é. */}
        {cadastrar.error ? <Aviso tipo="erro">{mensagemDeErro(cadastrar.error)}</Aviso> : null}

        {!perguntandoPreco && (
          <button type="button" className="botao-primario w-full" disabled={cadastrar.isPending}
                  onClick={tentar}>
            {cadastrar.isPending ? 'Cadastrando…' : 'Cadastrar peça'}
          </button>
        )}
      </form>
    </Layout>
  );
}
